import { ApiResponseError } from '../ApiResponseError';
import { WebIdProfileCheckResponse } from './model/WebIdProfileCheckResponse';
import { WebIdStartAutoIdentRequest } from './model/WebIdStartAutoIdentRequest';
import { WebIdStartAutoIdentResponse } from './model/WebIdStartAutoIdentResponse';
import { WebIdVerificationAttempt } from './model/WebIdVerificationAttempt';
import { UserVerifiedAt } from './model/UserVerifiedAt';

const postWebIdJson = async <ResponseBody>(
  path: string,
  body?: Record<string, string | boolean>,
): Promise<ResponseBody> => {
  const response = await fetch(
    `${process.env.WEBID_SERVICE_BASE_URL || 'https://random-webid-backend.example'}${path}`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Content-Type': 'application/json; charset=UTF-8',
      },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    },
  );

  if (!response.ok) {
    throw new ApiResponseError(response.status, response);
  }

  if (response.status === 204) {
    return {} as ResponseBody;
  }

  const responseText = await response.text();
  if (!responseText) {
    return {} as ResponseBody;
  }

  return JSON.parse(responseText) as ResponseBody;
};

export const startWebIdVerification = async (payload: WebIdVerificationAttempt): Promise<WebIdProfileCheckResponse> => {
  return postWebIdJson<WebIdProfileCheckResponse>('/api/trueid/check-user-profile', payload);
};

export const startWebIdAutoIdent = async (
  payload: WebIdStartAutoIdentRequest,
): Promise<WebIdStartAutoIdentResponse> => {
  return postWebIdJson<WebIdStartAutoIdentResponse>('/api/trueid/start-autoident', payload);
};

export const verifyWebIdTan = async (code: string): Promise<void> => {
  await postWebIdJson<void>('/api/trueid/tan/verify', { code });
};

export const sendWebIdTan = async (): Promise<void> => {
  await postWebIdJson<void>('/api/trueid/tan/send');
};

export const getWebIdUserVerifiedAt = async (): Promise<UserVerifiedAt> => {
  const response = await fetch(
    `${process.env.WEBID_SERVICE_BASE_URL || 'https://random-webid-backend.example'}/api/user/verified-at`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Content-Type': 'application/json; charset=UTF-8',
      },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new ApiResponseError(response.status, response);
  }

  return (await response.json()) as UserVerifiedAt;
};

export const getWebIdSessionVerifiedAt = async (sessionId: string): Promise<UserVerifiedAt> => {
  const response = await fetch(
    `${process.env.WEBID_SERVICE_BASE_URL || 'https://random-webid-backend.example'}/api/trueid/session/${sessionId}/verified-at`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Content-Type': 'application/json; charset=UTF-8',
      },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new ApiResponseError(response.status, response);
  }

  return (await response.json()) as UserVerifiedAt;
};
